import React, { ReactNode } from 'react';
import Input, { IInputProps } from '../../atoms/Input';
import { IControlProps, IPartialFormGroup } from '../types';
import FormControl from '../FormControl';
import { useHookFormController } from '../../hooks/useHookFormController';

export type IInputControlProps = IInputProps &
    IControlProps & {
        formGroupProps?: IPartialFormGroup;
        label?: ReactNode;
        tooltip?: ReactNode;
        required?: boolean;
        /**
         * Исторически: на вводе — число без пробелов (у текста NaN), при уходе — строка (и `''` при сбросе
         * крестиком); обработчики <changes> текстовых полей документа (Front Field.tsx) на этом держатся — не менять
         * без решения.
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onValueChange?: (value: any) => void;
        /**
         * `onValueChange` на вводе: `true` — `+value.replace(/\s/g, '')`, `false` — строка. Исторически: на вводе — число
         * без пробелов (у текста NaN), при уходе — строка; обработчики <changes> текстовых полей документа (Front
         * Field.tsx) на этом держатся — не менять без решения.
         * @default true
         */
        replaceOnValueChange?: boolean;
        /** Обрезать пробелы по краям на blur. false — для значений, где пробелы значимы (SQL, шаблоны, регулярки) */
        trimOnBlur?: boolean;
    };

const InputControl = ({
    name,
    rules,
    defaultValue,
    control,
    shouldUnregister,
    label,
    tooltip,
    required,
    formGroupProps,
    hideErrorMessage,
    replaceOnValueChange = true,
    trimOnBlur = true,
    onBlur,
    onValueChange,
    onClear,
    ...props
}: IInputControlProps) => {
    const Controller = useHookFormController();

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            shouldUnregister={shouldUnregister}
            defaultValue={defaultValue}
            render={({ field: { onChange, onBlur: rhfOnBlur, value, ...fieldProps } }) => (
                <FormControl
                    readonlyAppearance={props.readOnly || props.disabled ? 'input' : undefined}
                    name={name}
                    label={label}
                    tooltip={tooltip}
                    required={required}
                    control={control}
                    hideErrorMessage={hideErrorMessage}
                    {...formGroupProps}
                >
                    <Input
                        {...fieldProps}
                        {...props}
                        // поле без значения в данных формы (undefined) — пустое управляемое: иначе reset формы со
                        // значением переключает его из неуправляемого в управляемое, и React предупреждает
                        value={value ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            let val = e.target.value;
                            if (value === undefined || value === null || value.toString() != val.toString()) {
                                onChange(val);
                                if (onValueChange) {
                                    if (replaceOnValueChange) {
                                        onValueChange(+val.replace(/\s/g, ''));
                                    } else {
                                        onValueChange(val);
                                    }
                                }
                            }
                        }}
                        // второй вызов `onValueChange` — строкой при уходе (до обрезки пробелов) и `''` при сбросе
                        // крестиком — исторически делал `Input`; его `onValueChange` теперь на вводе, поэтому — здесь
                        onClear={
                            onClear &&
                            (() => {
                                onValueChange?.('');
                                onClear();
                            })
                        }
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                            onValueChange?.(e.currentTarget.value);
                            if (trimOnBlur && !props.readOnly && !props.disabled && typeof value === 'string') {
                                const trimmed = value.trim();
                                if (trimmed !== value) {
                                    onChange(trimmed);
                                }
                            }
                            rhfOnBlur();
                            onBlur?.(e);
                        }}
                    />
                </FormControl>
            )}
        />
    );
};

export default InputControl;
